// Turn-based combat in the style of Might and Magic III.
//
// Each round every conscious character and living monster acts once, in
// descending order of Speed. When a character's turn comes up the engine
// stops and waits for the player to choose an action; monster turns resolve
// automatically. All functions are pure: they take a snapshot of
// { party, monsters, combat, gold } and return a new one plus log messages.

import {
    isAble, characterAC, strikeHits, statBonus,
    meleeHitBonus, missileHitBonus, meleeDamage, missileDamage,
    applyCharacterDamage, healCharacter, rollDice,
} from './rules';
import { SPELLS, spellCost, castableOutsideCombat } from './spells';

const RUN_STUN_MOVES = 3;

function cloneState(state) {
    return {
        party: state.party.map((c) => ({ ...c })),
        monsters: state.monsters.map((m) => ({ ...m })),
        combat: state.combat ? { ...state.combat, order: [...state.combat.order], monsterIds: [...state.combat.monsterIds], blocking: { ...state.combat.blocking } } : null,
        gold: state.gold || 0,
        messages: [],
        outcome: null,
        // Missiles and spells cast this call, for the viewport to animate
        effects: [],
        // Cues for the sound effects, in the order things happened
        sounds: [],
        // Monsters killed this call, so the game can hand out what they carried
        kills: [],
    };
}

function cue(ctx, name) {
    ctx.sounds.push(name);
}

function effectTarget(monster) {
    return { id: monster.id, x: monster.x, y: monster.y, model: monster.model };
}

function recordShot(ctx, shooter, target, hit) {
    ctx.effects.push({
        type: 'missile',
        partyIndex: ctx.party.indexOf(shooter),
        projectile: shooter.missile.projectile || 'arrow',
        target: effectTarget(target),
        hit,
    });
}

function recordSpell(ctx, caster, spell, details) {
    ctx.effects.push({ type: 'spell', spellId: spell.id, partyIndex: ctx.party.indexOf(caster), ...details });
}

function say(ctx, message, type = 'combat') {
    ctx.messages.push({ type, message });
}

function findCharacter(ctx, id) {
    return ctx.party.find((c) => c.id === id);
}

function findMonster(ctx, id) {
    return ctx.monsters.find((m) => m.id === id);
}

function livingFoes(ctx) {
    return ctx.monsters.filter((m) => ctx.combat.monsterIds.includes(m.id) && m.hp > 0);
}

function buildOrder(ctx, rng) {
    const entries = [
        ...ctx.party.filter(isAble).map((c) => ({ id: c.id, speed: c.stats.speed })),
        ...livingFoes(ctx).map((m) => ({ id: m.id, speed: m.speed })),
    ].map((entry) => ({ ...entry, tie: rng() }));
    entries.sort((a, b) => b.speed - a.speed || b.tie - a.tie);
    return entries.map((entry) => entry.id);
}

export function currentActorId(combat) {
    return combat ? combat.order[combat.turn] : null;
}

function conditionMessage(ctx, character) {
    if (character.condition === 'dead') {
        say(ctx, `${character.name} has been slain!`, 'hurt');
    } else if (character.condition === 'unconscious') {
        say(ctx, `${character.name} falls unconscious!`, 'hurt');
    }
}

// Kills feed the combat's reward pool, or the volley's pool outside combat
function damageMonster(ctx, monster, amount, pool = ctx.combat) {
    monster.hp -= amount;
    monster.hitCount = (monster.hitCount || 0) + 1;
    if (monster.hp <= 0) {
        ctx.kills.push({ name: monster.name, type: monster.type });
        cue(ctx, 'monsterDies');
        say(ctx, `The ${monster.name} is destroyed!`, 'victory');
        pool.xpPool += monster.xp;
        pool.goldPool += monster.gold;
        ctx.monsters = ctx.monsters.filter((m) => m.id !== monster.id);
    }
}

function monsterTurn(ctx, monster, rng) {
    for (let i = 0; i < monster.attacks; i++) {
        const targets = ctx.party.filter(isAble);
        if (!targets.length) return;
        const target = targets[Math.floor(rng() * targets.length)];
        const ac = characterAC(target, !!ctx.combat.blocking[target.id]);
        if (!strikeHits(monster.accuracy, ac, rng)) {
            cue(ctx, 'meleeMiss');
            say(ctx, `The ${monster.name} swings at ${target.name} and misses.`);
            continue;
        }
        const damage = rollDice(monster.damage, rng);
        applyCharacterDamage(target, damage);
        cue(ctx, 'partyHurt');
        say(ctx, `The ${monster.name} hits ${target.name} for ${damage} damage!`, 'hurt');
        if (target.condition !== 'good') cue(ctx, 'partyDown');
        conditionMessage(ctx, target);
    }
}

// Split a reward pool's experience among conscious characters and bank its gold
function awardPool(ctx, pool) {
    const survivors = ctx.party.filter(isAble);
    const share = survivors.length ? Math.floor(pool.xpPool / survivors.length) : 0;
    survivors.forEach((c) => { c.xp += share; });
    ctx.gold += pool.goldPool;
    return share;
}

// Returns true when the fight is over (and settles rewards / defeat)
function checkEnd(ctx) {
    if (!ctx.combat) return true;
    if (!livingFoes(ctx).length) {
        const share = awardPool(ctx, ctx.combat);
        cue(ctx, 'victory');
        say(ctx, `Victory! Each survivor gains ${share} experience. The party finds ${ctx.combat.goldPool} gold.`, 'victory');
        ctx.combat = null;
        ctx.outcome = 'victory';
        return true;
    }
    if (!ctx.party.some(isAble)) {
        cue(ctx, 'defeat');
        say(ctx, 'Your party has fallen. The dungeon claims another band of adventurers...', 'hurt');
        ctx.combat = null;
        ctx.outcome = 'defeat';
        return true;
    }
    return false;
}

function advanceTurn(ctx, rng) {
    ctx.combat.turn++;
    if (ctx.combat.turn >= ctx.combat.order.length) {
        ctx.combat.round++;
        ctx.combat.turn = 0;
        ctx.combat.order = buildOrder(ctx, rng);
    }
}

// Resolve monster turns (and skip incapacitated actors) until a character may act
function runUntilPartyTurn(ctx, rng) {
    for (let guard = 0; guard < 1000; guard++) {
        if (checkEnd(ctx)) return;
        const id = currentActorId(ctx.combat);
        const monster = findMonster(ctx, id);
        if (monster) {
            monsterTurn(ctx, monster, rng);
            advanceTurn(ctx, rng);
            continue;
        }
        const character = findCharacter(ctx, id);
        if (!character || !isAble(character)) {
            advanceTurn(ctx, rng);
            continue;
        }
        // A block lasts until the character's next turn
        delete ctx.combat.blocking[character.id];
        return;
    }
}

function pickTarget(ctx, targetId) {
    const foes = livingFoes(ctx);
    return foes.find((m) => m.id === targetId) || foes[0];
}

function mostWoundedAlly(ctx) {
    const living = ctx.party.filter((c) => c.condition !== 'dead' && c.hp < c.maxHp);
    living.sort((a, b) => a.hp / a.maxHp - b.hp / b.maxHp);
    return living[0] || null;
}

// Why a character can't receive a healing spell, or null if they can
export function healBlocker(character, spell) {
    if (character.condition === 'dead') return `${character.name} is beyond the help of ${spell.name}.`;
    if (character.hp >= character.maxHp) return `${character.name} is not wounded.`;
    return null;
}

// Cast a known spell. Healing goes to `options.allyId`, or the most wounded
// character if none was chosen. Returns false if the spell could not be cast.
function castSpell(ctx, actor, options, rng) {
    const spell = SPELLS[options.spellId];
    if (!spell || !actor.spells.includes(spell.id)) {
        say(ctx, `${actor.name} doesn't know that spell.`, 'dev');
        return false;
    }
    const cost = spellCost(spell.id, actor);
    if (actor.sp < cost) {
        say(ctx, `${actor.name} doesn't have enough spell points for ${spell.name}.`, 'dev');
        return false;
    }
    if (spell.target === 'party') {
        const wounded = ctx.party.filter((c) => c.condition !== 'dead' && c.hp < c.maxHp);
        if (!wounded.length) {
            say(ctx, 'Nobody needs healing.', 'dev');
            return false;
        }
        actor.sp -= cost;
        cue(ctx, 'heal');
        let total = 0;
        wounded.forEach((ally) => {
            recordSpell(ctx, actor, spell, { allyIndex: ctx.party.indexOf(ally) });
            total += healCharacter(ally, spell.heal(actor, rng));
        });
        say(ctx, `${actor.name} casts ${spell.name}. The party recovers ${total} HP.`);
        return true;
    }

    if (spell.target === 'allEnemies') {
        const foes = livingFoes(ctx);
        if (!foes.length) return false;
        actor.sp -= cost;
        cue(ctx, 'spellHit');
        say(ctx, `${actor.name} casts ${spell.name}!`);
        // Copy the list first: a foe that dies is taken out of the fight
        foes.slice().forEach((foe) => {
            recordSpell(ctx, actor, spell, { target: effectTarget(foe) });
            const damage = spell.damage(actor, rng);
            say(ctx, `The bolt strikes the ${foe.name} for ${damage} damage!`);
            damageMonster(ctx, foe, damage);
        });
        return true;
    }

    if (spell.target === 'ally') {
        const ally = options.allyId ? findCharacter(ctx, options.allyId) : mostWoundedAlly(ctx);
        if (!ally) {
            say(ctx, 'No one needs healing.', 'dev');
            return false;
        }
        const blocker = healBlocker(ally, spell);
        if (blocker) {
            say(ctx, blocker, 'dev');
            return false;
        }
        actor.sp -= cost;
        cue(ctx, 'heal');
        recordSpell(ctx, actor, spell, { allyIndex: ctx.party.indexOf(ally) });
        const healed = healCharacter(ally, spell.heal(actor, rng));
        say(ctx, `${actor.name} casts ${spell.name}. ${ally.name} recovers ${healed} HP.`);
        return true;
    }
    actor.sp -= cost;
    const target = pickTarget(ctx, options.targetId);
    cue(ctx, 'spellHit');
    recordSpell(ctx, actor, spell, { target: effectTarget(target) });
    const damage = spell.damage(actor, rng);
    say(ctx, `${actor.name} casts ${spell.name} on the ${target.name} for ${damage} damage!`);
    damageMonster(ctx, target, damage);
    return true;
}

// Perform one character action. Returns false if the action was not possible
// (so the character keeps their turn).
function doAction(ctx, actor, action, options, rng) {
    switch (action) {
        case 'attack': {
            const target = pickTarget(ctx, options.targetId);
            if (!strikeHits(meleeHitBonus(actor), target.ac, rng)) {
                cue(ctx, 'meleeMiss');
                say(ctx, `${actor.name} attacks the ${target.name} and misses.`);
                return true;
            }
            cue(ctx, 'meleeHit');
            const damage = meleeDamage(actor, rng);
            say(ctx, `${actor.name} hits the ${target.name} with a ${actor.weapon.name} for ${damage} damage!`);
            damageMonster(ctx, target, damage);
            return true;
        }
        case 'shoot': {
            if (!actor.missile) {
                say(ctx, `${actor.name} has no missile weapon!`, 'dev');
                return false;
            }
            const target = pickTarget(ctx, options.targetId);
            if (!strikeHits(missileHitBonus(actor), target.ac, rng)) {
                recordShot(ctx, actor, target, false);
                cue(ctx, 'meleeMiss');
                say(ctx, `${actor.name} fires a ${actor.missile.name} at the ${target.name} and misses.`);
                return true;
            }
            recordShot(ctx, actor, target, true);
            cue(ctx, 'arrowHit');
            const damage = missileDamage(actor, rng);
            say(ctx, `${actor.name} shoots the ${target.name} for ${damage} damage!`);
            damageMonster(ctx, target, damage);
            return true;
        }
        case 'cast':
            return castSpell(ctx, actor, options, rng);
        case 'use': {
            const potion = actor.backpack.find((i) => i.id === options.itemId && i.restores);
            if (!potion) {
                say(ctx, `${actor.name} has nothing like that to hand.`, 'dev');
                return false;
            }
            const toHp = potion.restores === 'hp';
            const room = toHp ? actor.maxHp - actor.hp : actor.maxSp - actor.sp;
            if (room <= 0) {
                say(ctx, `${actor.name} has no need of it yet.`, 'dev');
                return false;
            }
            const restored = Math.min(potion.amount, room);
            if (toHp) {
                actor.hp += restored;
            } else {
                actor.sp += restored;
            }
            actor.backpack = actor.backpack.filter((i) => i.id !== potion.id);
            cue(ctx, 'heal');
            recordSpell(ctx, actor, { id: 'firstAid' }, { allyIndex: ctx.party.indexOf(actor) });
            say(ctx, `${actor.name} drinks the ${potion.kind} and recovers ${restored} ` +
                     `${toHp ? 'hit points' : 'spell points'}.`);
            return true;
        }
        case 'wait': {
            say(ctx, `${actor.name} waits.`);
            return true;
        }
        case 'block': {
            ctx.combat.blocking[actor.id] = true;
            cue(ctx, 'block');
            say(ctx, `${actor.name} raises a guard.`);
            return true;
        }
        case 'run': {
            const able = ctx.party.filter(isAble);
            const partySpeed = able.reduce((sum, c) => sum + c.stats.speed, 0) / able.length;
            const foeSpeed = Math.max(...livingFoes(ctx).map((m) => m.speed));
            const chance = Math.min(90, Math.max(10, 50 + (partySpeed - foeSpeed) * 4 + statBonus(actor.stats.luck) * 2));
            if (rng() * 100 < chance) {
                livingFoes(ctx).forEach((m) => { m.stunned = RUN_STUN_MOVES; });
                cue(ctx, 'flee');
                say(ctx, 'The party flees from combat!', 'voice');
                ctx.combat = null;
                ctx.outcome = 'fled';
            } else {
                say(ctx, `${actor.name} tries to find an escape route, but the way is blocked!`);
            }
            return true;
        }
        default:
            return false;
    }
}

export function startCombat(state, monsterIds, rng = Math.random) {
    const ctx = cloneState(state);
    ctx.combat = { round: 1, turn: 0, order: [], monsterIds: [...monsterIds], blocking: {}, xpPool: 0, goldPool: 0 };
    ctx.combat.order = buildOrder(ctx, rng);
    cue(ctx, 'combatStart');
    livingFoes(ctx).forEach((m) => say(ctx, `A ${m.name} attacks!`, 'hurt'));
    runUntilPartyTurn(ctx, rng);
    return ctx;
}

// Monsters that reach the party mid-fight join in; they act from the next round
export function joinCombat(state, monsterIds) {
    const ctx = cloneState(state);
    if (!ctx.combat) return ctx;
    monsterIds.filter((id) => !ctx.combat.monsterIds.includes(id)).forEach((id) => {
        ctx.combat.monsterIds.push(id);
        const monster = findMonster(ctx, id);
        if (monster) say(ctx, `A ${monster.name} joins the fight!`, 'hurt');
    });
    return ctx;
}

// MM3's Shoot while exploring: every conscious character with a missile
// weapon fires at a monster that is in sight but not yet in melee range
export function rangedVolley(state, targetId, rng = Math.random) {
    const ctx = cloneState(state);
    const pool = { xpPool: 0, goldPool: 0 };
    const shooters = ctx.party.filter((c) => isAble(c) && c.missile);
    if (!shooters.length) {
        say(ctx, 'No one in the party has a missile weapon!', 'dev');
        return ctx;
    }
    for (const shooter of shooters) {
        const target = findMonster(ctx, targetId);
        if (!target) break;
        if (!strikeHits(missileHitBonus(shooter), target.ac, rng)) {
            recordShot(ctx, shooter, target, false);
            say(ctx, `${shooter.name} fires a ${shooter.missile.name} at the ${target.name} and misses.`);
            continue;
        }
        recordShot(ctx, shooter, target, true);
        cue(ctx, 'arrowHit');
        const damage = missileDamage(shooter, rng);
        say(ctx, `${shooter.name} shoots the ${target.name} for ${damage} damage!`);
        damageMonster(ctx, target, damage, pool);
    }
    if (pool.xpPool > 0) {
        const share = awardPool(ctx, pool);
        say(ctx, `Each character gains ${share} experience. The party finds ${pool.goldPool} gold.`, 'victory');
    }
    return ctx;
}

// Cast a spell while exploring. Only spells aimed at the party work outside
// combat. `cast` on the result says whether the spell went off.
export function castOutsideCombat(state, casterId, spellId, options = {}, rng = Math.random) {
    const ctx = cloneState(state);
    ctx.cast = false;
    const caster = findCharacter(ctx, casterId);
    const spell = SPELLS[spellId];
    if (!caster || !isAble(caster)) {
        say(ctx, `${caster ? caster.name : 'That character'} is in no condition to cast.`, 'dev');
    } else if (!castableOutsideCombat(spell)) {
        say(ctx, `${spell ? spell.name : 'That spell'} can only be cast in combat.`, 'dev');
    } else {
        ctx.cast = castSpell(ctx, caster, { ...options, spellId }, rng);
    }
    return ctx;
}

export function performPartyAction(state, action, options = {}, rng = Math.random) {
    const ctx = cloneState(state);
    if (!ctx.combat) return ctx;
    const actor = findCharacter(ctx, currentActorId(ctx.combat));
    if (!actor) return ctx;

    if (action === 'quick') {
        // Quick Fight: every character still to act this round attacks
        const round = ctx.combat.round;
        let current = actor;
        while (current && ctx.combat && ctx.combat.round === round) {
            doAction(ctx, current, 'attack', options, rng);
            if (checkEnd(ctx)) break;
            advanceTurn(ctx, rng);
            runUntilPartyTurn(ctx, rng);
            current = ctx.combat && findCharacter(ctx, currentActorId(ctx.combat));
        }
        return ctx;
    }

    if (!doAction(ctx, actor, action, options, rng)) return ctx;
    if (checkEnd(ctx)) return ctx;
    advanceTurn(ctx, rng);
    runUntilPartyTurn(ctx, rng);
    return ctx;
}
